/**
 * Auth token storage for Sanctum personal access tokens.
 *
 * Strategy: memory-first (set on login, cleared on logout) with a localStorage
 * mirror so the user stays signed in across page refreshes / new tabs. The
 * impersonation token is stored separately so an admin can switch back to
 * their original session.
 *
 * Token storage in localStorage is XSS-exposed; this is the standard
 * trade-off for SPA token auth. Mitigation: strict CSP in production.
 */

const TOKEN_KEY = 'optimancer_auth_token';
const ADMIN_TOKEN_KEY = 'optimancer_admin_token'; // stashed during impersonation

let memoryToken: string | null = null;
let memoryAdminToken: string | null = null;

function readLocalStorage(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeLocalStorage(key: string, value: string | null): void {
  try {
    if (value === null) {
      localStorage.removeItem(key);
    } else {
      localStorage.setItem(key, value);
    }
  } catch {
    // localStorage unavailable (private browsing, quota); memory fallback only.
  }
}

export function getToken(): string | null {
  if (memoryToken !== null) return memoryToken;
  memoryToken = readLocalStorage(TOKEN_KEY);
  return memoryToken;
}

export function setToken(token: string | null): void {
  memoryToken = token;
  writeLocalStorage(TOKEN_KEY, token);
}

export function clearToken(): void {
  setToken(null);
}

export function stashAdminToken(token: string): void {
  memoryAdminToken = token;
  writeLocalStorage(ADMIN_TOKEN_KEY, token);
}

export function popAdminToken(): string | null {
  const t = memoryAdminToken ?? readLocalStorage(ADMIN_TOKEN_KEY);
  memoryAdminToken = null;
  writeLocalStorage(ADMIN_TOKEN_KEY, null);
  return t;
}

export function hasAdminToken(): boolean {
  return (memoryAdminToken ?? readLocalStorage(ADMIN_TOKEN_KEY)) !== null;
}
